import {
    Alert,
    Badge,
    Box,
    Button,
    ColumnLayout,
    FormField,
    Input,
    RadioGroup,
    RadioGroupProps,
    SpaceBetween
} from "@amzn/awsui-components-react";
import * as React from "react";
import { useAPI2 } from "../../../hooks/api-hook";
import { AppContext } from "../../../setup/context";
import {
    APIOutput,
    AppError,
    DecisionTreeContent,
    DecisionTreeNode,
    EntityRef,
    ErrorCode,
    IDecisionTree,
    NodeAnswerDisplayType,
    PrimitiveDataFactory
} from "@amzn/ask-legal-domain";
import { UIModel } from "../../../model/ui-model";
import { UIField } from "../../common/UIField";
import { ErrorFlashbar } from "../../common/ErrorFlashbar";
import { Builder } from "builder-pattern";
import { LoadingSpinner } from "../../common/loading-spinner";
import { CustomCommonContainerModal } from "../../container/CommonContainerModal";
import { StrongRichTextEdit } from "../../common/RichTextEditor/TinyMCEBased/StrongRichTextEdit";
import { SelectExistingNodeModal } from "./SelectExistingNodeModal";
import { filterLinkableNodes, LinkableNode } from "../../../utils/decisiontree/linkable-nodes-filter";
import { isEdge } from "../../../utils/decisiontree/edge-match";

export const NodeEditModal = (props: {
    draftRef: EntityRef;
    nodeId: string;
    containerId: string;
    content: DecisionTreeContent;
    onDismiss: () => void;
    onSucceed: (output: IDecisionTree.UpdateDraftOutput) => void;
}) => {
    const context = React.useContext(AppContext);
    const getNodeRunner = useAPI2(context.getDecisionTreeNodeAPI().get);
    const updateNodeRunner = useAPI2(context.getDecisionTreeNodeAPI().update);
    const [loadedNode, setLoadedNode] = React.useState<DecisionTreeNode>(null);
    const [showLinkModal, setShowLinkModal] = React.useState(false);
    const [linkableNodes, setLinkableNodes] = React.useState<LinkableNode[]>([]);

    const overviewText = UIModel.State.useNotNullString({ initialValue: "" });
    const question = UIModel.State.useNotNullString({ initialValue: "" });
    const answerDisplayStyle = UIModel.State.use({ initialValue: NodeAnswerDisplayType.Buttons });

    const children = UIModel.State.useArray<{
        answer: string;
        nodeId: string;
        new: boolean; // to indicate this child is newly created or not
        type: "new" | "link"; // NEW: type of child node
        linkedNodeOverview?: string; // NEW: for display when type is "link"
    }>({ initialValue: [] });

    const [selectedAnswerIndexForLink, setSelectedAnswerIndexForLink] = React.useState<number | null>(null);

    const childrenToDelete = UIModel.State.useArray<{
        answer: string;
        nodeId: string;
    }>({ initialValue: [] });

    React.useEffect(() => {
        getNodeRunner.invoke({
            containerId: props.containerId,
            nodeId: props.nodeId
        });
    }, []);

    React.useEffect(() => {
        if (getNodeRunner.status === "Succeeded") {
            const nodeData = getNodeRunner.output;
            setLoadedNode(nodeData.metadata);
            question.setValue(nodeData.content.contentValue);
            overviewText.setValue(nodeData.metadata.overviewText);
            children.setValue(nodeData.metadata.children.map(child => ({
                answer: child.answer,
                nodeId: child.nodeId,
                new: false,
                type: "new" as "new" | "link" // Existing children default to "new" type
            })));
            answerDisplayStyle.setValue(
                nodeData.metadata.answerDisplayType
                || NodeAnswerDisplayType.Buttons
            );
        }
    }, [getNodeRunner.status]);

    const onSubmit = () => {
        const updateNodeInput = IDecisionTree.UpdateDraftInput.create({
            draftRef: props.draftRef,
            containerId: props.containerId,
            nodeId: props.nodeId,
            overviewText: overviewText.value,
            question: question.value,
            children: children.value
                // Match on (nodeId, answer) so removing one convergent edge (same child,
                // different answer) does not drop the sibling edges to that same child.
                .filter(child => childrenToDelete.value.every(
                    c => !isEdge(c, child.nodeId, child.answer)
                ))
                .map(child => ({
                    answer: child.answer,
                    nodeId: child.nodeId
                })),
            answerDisplayType: answerDisplayStyle.value
        });
        updateNodeRunner.invoke(updateNodeInput);
    };

    React.useEffect(() => {
        if (updateNodeRunner.status === "Succeeded" && updateNodeRunner.output) {
            props.onSucceed(updateNodeRunner.output);
        }
    }, [updateNodeRunner.status]);

    // Handle "Link to Existing Node" button click
    const handleLinkToExisting = () => {
        // Filter linkable nodes using frontend logic (no API call needed)
        const filtered = filterLinkableNodes(props.nodeId, props.content);
        setLinkableNodes(filtered);
        setShowLinkModal(true);
    };

    // Handle node selection from modal
    const handleNodeSelected = (selectedNode: LinkableNode) => {
        if (selectedAnswerIndexForLink !== null) {
            // Update existing answer to link to selected node
            const updatedAnswers = children.value.slice();
            updatedAnswers[selectedAnswerIndexForLink] = {
                ...updatedAnswers[selectedAnswerIndexForLink],
                nodeId: selectedNode.nodeId,
                type: "link",
                linkedNodeOverview: selectedNode.overviewText
            };
            children.setValue(updatedAnswers);
            setSelectedAnswerIndexForLink(null);
        }
        setShowLinkModal(false);
    };

    return <>
        <SelectExistingNodeModal
            visible={showLinkModal}
            linkableNodes={linkableNodes}
            loading={false}
            error={undefined}
            onDismiss={() => {
                // No rollback needed — type is only updated on successful selection
                setSelectedAnswerIndexForLink(null);
                setShowLinkModal(false);
            }}
            onSelect={handleNodeSelected}
        />
        <CustomCommonContainerModal
            size="xl"
            header="Edit Node"
            onCancel={props.onDismiss}
            onSave={onSubmit}
            loading={updateNodeRunner.status === "Running"}
            disabled={
                getNodeRunner.status === "Running" ||
                children.value.some(x => x.answer.length === 0) ||
                !!overviewText.errorText ||
                !!question.errorText
            }
            child={<>
                {/* Error display */}
                {!!updateNodeRunner.err && (
                    <Box margin={{ bottom: "s" }}>
                        {updateNodeRunner.err.details?.code ? (
                            <Alert
                                type="error"
                                header="Validation Failed"
                            >
                                <SpaceBetween size="s">
                                    <Box variant="p">
                                        <strong>Error:</strong> {updateNodeRunner.err.message}
                                    </Box>
                                    {updateNodeRunner.err.details.suggestion && (
                                        <Box variant="p" color="text-status-info">
                                            <strong>Suggestion:</strong> {updateNodeRunner.err.details.suggestion}
                                        </Box>
                                    )}
                                    {updateNodeRunner.err.details.validationCode && (
                                        <Box variant="small" color="text-body-secondary">
                                            Error Code: {updateNodeRunner.err.details.validationCode}
                                        </Box>
                                    )}
                                </SpaceBetween>
                            </Alert>
                        ) : (
                            <ErrorFlashbar error={updateNodeRunner.err}/>
                        )}
                    </Box>
                )}
            {getNodeRunner.status === "Running" && <LoadingSpinner/>}
            {!!loadedNode && <ColumnLayout columns={1} borders="horizontal">
                <UIField.StateValueField
                    name="Node overview text"
                    state={overviewText}
                    editing={true}
                    variant="Input"
                    constraintText="Description text"
                />
                <UIField.CustomField
                    name={`Question`}
                    child={<StrongRichTextEdit
                        content={question.value}
                        onChange={updated => question.setValue(updated)}
                        hidePermContent={true}
                    />}
                    errorText={question.errorText}
                />
                <UIField.CustomField
                    name="Answer Display Style"
                    child={<RadioGroup
                        onChange={({ detail }) => {
                            answerDisplayStyle.setValue(detail.value);
                        }}
                        value={answerDisplayStyle.value}
                        items={[
                            { label: "Buttons", value: NodeAnswerDisplayType.Buttons },
                            { label: "Dropdown", value: NodeAnswerDisplayType.Dropdown }
                        ]}
                    />}
                />
                <SpaceBetween direction="vertical" size="m">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="primary" iconName="add-plus" onClick={() => {
                            children.setValue([
                                ...children.value,
                                {
                                    answer: "Default Answer",
                                    nodeId: PrimitiveDataFactory.id(),
                                    new: true,
                                    type: "new" as "new" | "link"
                                }
                            ]);
                        }}>Add New Answer</Button>
                    </SpaceBetween>
                    {children.value.map((child, index) =>
                        <FormField
                            errorText={child.answer.length === 0 && "Value cannot be empty"}
                            label={<Box margin={{ bottom: "xxxs" }} color="text-label">
                                Answer&nbsp;
                                { childrenToDelete.value.some(c => isEdge(c, child.nodeId, child.answer)) ?
                                    <span><Badge color="red">Unlinked</Badge>&nbsp;This link will be removed. The child node will become unlinked and can be reconnected later.</span> :
                                    child.type === "link" ?
                                        <span>
                                            <Badge color="blue">Link</Badge>&nbsp;
                                            Links to "{child.linkedNodeOverview}"
                                        </span> :
                                        (child.new && <Badge color="green">New</Badge>)
                                }
                            </Box>}>
                                <SpaceBetween direction="vertical" size="xs">
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <div style={{ width: "400px" }}>
                                            <Input
                                                value={child.answer}
                                                onChange={(e: { detail: { value: string; }; }) => {
                                                    const updatedAnswer = Builder(child)
                                                        .answer(e.detail.value)
                                                        .build();
                                                    const updatedAnswers = children.value.slice();
                                                    updatedAnswers.splice(index, 1, updatedAnswer);
                                                    children.setValue(updatedAnswers);
                                                }}
                                                disabled={childrenToDelete.value.some(c => isEdge(c, child.nodeId, child.answer))}
                                            />
                                        </div>
                                        <RadioGroup
                                            value={child.type}
                                            onChange={({ detail }) => {
                                                if (detail.value === "link") {
                                                    // Don't update type yet — wait for successful node selection.
                                                    // This avoids the need for rollback if the user cancels the modal.
                                                    setSelectedAnswerIndexForLink(index);
                                                    handleLinkToExisting();
                                                } else {
                                                    // User wants new node
                                                    const updatedAnswers = children.value.slice();
                                                    updatedAnswers[index] = {
                                                        ...updatedAnswers[index],
                                                        nodeId: PrimitiveDataFactory.id(),
                                                        type: "new",
                                                        linkedNodeOverview: undefined
                                                    };
                                                    children.setValue(updatedAnswers);
                                                }
                                            }}
                                            items={[
                                                { label: "New Node", value: "new" },
                                                { label: "Link to Existing", value: "link" }
                                            ]}
                                        />
                                        {childrenToDelete.value.every(c => !isEdge(c, child.nodeId, child.answer)) && <Button iconName="remove" onClick={() =>
                                            childrenToDelete.setValue([...childrenToDelete.value, child])
                                        }>Remove Link</Button>}
                                        {childrenToDelete.value.some(c => isEdge(c, child.nodeId, child.answer)) && <Button onClick={() =>
                                            childrenToDelete.setValue(
                                                childrenToDelete.value.filter(c => !isEdge(c, child.nodeId, child.answer))
                                            )
                                        }>Recover</Button>}
                                    </SpaceBetween>
                                </SpaceBetween>
                        </FormField>
                    )}
                </SpaceBetween>
            </ColumnLayout>}</>
            }
        />
    </>;
};
